import { motion } from "framer-motion";
import { Building2, Users2, Store } from "lucide-react";

const WAREHOUSE_IMG =
  "https://images.pexels.com/photos/9305407/pexels-photo-9305407.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const WORKERS_IMG =
  "https://images.unsplash.com/photo-1759272548449-7b689a81c8fb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwzfHxsb2dpc3RpY3MlMjB3b3JrZXJ8ZW58MHx8fHwxNzg1MDQ3ODA0fDA&ixlib=rb-4.1.0&q=85";

const GROUPS = [
  {
    icon: Building2,
    img: WAREHOUSE_IMG,
    title: "3PL Owners & Warehouse Teams",
    body: "Owners run the tenant. Managers orchestrate the floor. Staff execute orders, inbound and returns — nothing more, nothing less.",
    roles: ["3PL Owner", "Warehouse Manager", "Warehouse Staff"],
  },
  {
    icon: Users2,
    img: WORKERS_IMG,
    title: "VA Agencies",
    body: "Agencies see only the stores they're assigned. Managers direct, staff execute — scoped to the store, blocked everywhere else.",
    roles: ["VA Agency Owner", "VA Manager", "VA Staff"],
  },
  {
    icon: Store,
    img: null,
    title: "Clients",
    body: "Your e-commerce clients get a clean, read-only window into their own stores. Full transparency, zero risk.",
    roles: ["Client (read-only)"],
  },
];

export const RolesSection = () => (
  <section id="roles" className="relative py-24 lg:py-32 bg-white border-t border-slate-200 overflow-hidden" data-testid="roles-section">
    <div className="lp-orb w-[500px] h-[400px] bg-indigo-200/60 top-20 -right-40" />
    <div className="max-w-7xl mx-auto px-5 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
      <div className="lg:col-span-5">
        <div className="lg:sticky lg:top-32">
          <p className="text-xs md:text-sm uppercase tracking-[0.3em] text-indigo-600 font-bold mb-4">Access Control</p>
          <h2 className="lp-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#0b1530] leading-[1.05]">
            8 Roles.
            <br />
            One Platform.
            <br />
            <span className="lp-gradient-text">Zero leakage.</span>
          </h2>
          <p className="mt-7 text-base md:text-lg text-slate-600 leading-relaxed max-w-md">
            Permissions are enforced server-side on every endpoint. A VA can't touch a store outside
            their assignment. A client can't see another client's orders. Ever.
          </p>
        </div>
      </div>

      <div className="lg:col-span-7 space-y-6">
        {GROUPS.map((g, i) => (
          <motion.div
            key={g.title}
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: i * 0.1, ease: "easeOut" }}
            className="lp-card relative rounded-2xl border border-slate-200 overflow-hidden hover:border-indigo-200 hover:shadow-xl hover:shadow-indigo-600/10 transition-all duration-300"
            data-testid={`role-card-${i}`}
          >
            {g.img && (
              <>
                <img src={g.img} alt={g.title} className="absolute inset-0 w-full h-full object-cover opacity-25" />
                <div className="absolute inset-0 bg-gradient-to-r from-white via-white/90 to-white/60" />
              </>
            )}
            <div className="relative p-8 lg:p-9">
              <span className="lp-icon-chip mb-5"><g.icon className="text-indigo-600" size={24} strokeWidth={1.5} /></span>
              <h3 className="lp-display text-xl md:text-2xl font-semibold tracking-tight text-[#0b1530]">{g.title}</h3>
              <p className="mt-3 text-[15px] md:text-base text-slate-600 leading-relaxed max-w-lg">{g.body}</p>
              <div className="mt-6 flex flex-wrap gap-2">
                {g.roles.map((r) => (
                  <span key={r} className="text-xs font-semibold uppercase tracking-[0.15em] text-indigo-700 border border-indigo-200 bg-indigo-50 rounded-full px-3.5 py-1.5">
                    {r}
                  </span>
                ))}
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);
